import { Injectable } from '@angular/core';
import { LanguageService } from '../languageService/language.service';
import { RequestService } from '../requestService/request.service';

@Injectable({
  providedIn: 'root'
})
export class SubscriberService {

  constructor(private requestService: RequestService, private languageService: LanguageService) { }

  // Login to the service
  login(params) {
    let req = {
      url: "/api/auth/login",
      req: {
        username: params.username,
        password: params.password
      }
    };
    return this.requestService.mainPostRequest(req)
  }

  // Login to the service as guest
  loginGuest(params, userService) {
    let req = {
      url: "/api/auth/guest",
      req: {
        language: params.language
      }
    };
    return new Promise(resolve => {
      // Log In as Guest
      this.requestService.mainPostRequest(req).then((resp: any) => {
        // Update Token
        userService.getToken().then(respToken => {
          resolve(resp);
        });
      })
    });
  }

  // Logout from the service
  logOut(userService) {
    let req = {
      url: "/api/auth/logout"
    };
    return new Promise(resolve => {
      // Log Out
      this.requestService.mainGetRequest(req).then((resp: any) => {
        userService.onLogout();
        // Update Token
        userService.getToken().then(respToken => {
          resolve(resp);
        });
      })
    });
  }

  // Register into the service
  register(params) {
    let req = {
      url: "/api/auth/register",
      req: {
        registration: {
          email: params.email,
          password: params.password,
          language: params.language,
          coupon: params.coupon
        }
      }
    };
    return this.requestService.mainPostRequest(req);
  }

  // Validates registration PIN 
  validateRegisterPin(params) {
    let req = {
      url: "/api/auth/registerPin",
      req: {
        email: params.email,
        password: params.password,
        pin: params.pin
      }
    };
    return this.requestService.mainPostRequest(req);
  }

  // Forgot Password action
  forgotPassword(params) {
    let req = {
      url: "/api/subscribers/forgotPassword",
      req: {
        email: params.email
      }
    };
    this.requestService.mainPostRequest(req).then((resp: any) => {
      this.languageService.openSnackBar("msg_reset_email_sent", {});
    })
  }

  // Reset Password
  resetPassword(params) {
    let req = {
      url: "/api/subscribers/resetPassword",
      req: {
        token: params.token,
        password: params.password
      }
    };
    this.requestService.mainPostRequest(req).then((resp: any) => {
      this.languageService.openSnackBar("info_reset_password_success", {});
    })
  }

  // Change Email action
  changeEmail(params) {
    let req = {
      url: "/api/subscribers/changeEmail",
      req: {
        newEmail: params.email
      }
    };

    return new Promise(resolve => {
      this.requestService.mainPostRequest(req).then((resp: any) => {
        resolve(resp);
        this.languageService.openSnackBar("msg_reset_email_sent", {});
      })
    });
  }

  // Validates change email PIN 
  validateChangeEmailPin(params) {
    let req = {
      url: "/api/subscribers/changeEmailPin",
      req: {
        newEmail: params.email,
        newEmailPin: params.newPin,
        oldEmailPin: params.oldPin
      }
    };
    return this.requestService.mainPostRequest(req);
  }

  // Change Password action
  changePassword(params) {
    let req = {
      url: "/api/subscribers/changePassword",
      req: {
        oldPassword: params.oldPassword,
        newPassword: params.newPassword
      }
    };

    return new Promise(resolve => {
      this.requestService.mainPostRequest(req).then((resp: any) => {
        resolve(resp);
        this.languageService.openSnackBar("info_change_password_success", {});
      })
    });
  }

  // Change OnBoarded action
  changeOnBoarded() {
    let req = {
      url: "/api/subscribers/editOnboarded",
      req: {
        onboarded: true
      }
    };

    return new Promise(resolve => {
      this.requestService.mainPostRequest(req).then((resp: any) => {
        resolve(resp);
      })
    });
  }

  // Return billing history
  getBillingHistory(params) {
    let req = {
      url: "/api/subscribers/billingHistory",
      req: {
        count: params.count,
        offset: params.offset,
        productTypes: [0],
        includeExpired: true
      }
    };
    return new Promise(resolve => {
      this.requestService.mainPostRequest(req).then((resp: any) => {
        resolve({
          billHistory: resp.data.subscriptions,
          nextPage: resp.data.nextPage
        })
      });
    });
  }

  /* Profile Related */
  // Get profiles from platform
  getProfiles() {
    let req = {
      url: "/api/subscribers/getProfiles"
    };
    return this.requestService.mainGetRequest(req);
  }

  // Get profiles from platform
  getProfile(params) {
    let req = {
      url: "/api/subscribers/getProfile",
      req: {
        profileId: params.id
      }
    };
    return this.requestService.mainPostRequest(req);
  }

  // Create New Profile
  createProfile(params) {
    let req = {
      url: "/api/subscribers/createProfile",
      req: {
        profile: {
          type: params.type,
          displayName: params.displayName,
          avatar: params.avatar,
          rating: params.rating,
          language: params.language,
          pinEnabled: params.pinEnabled,
          pin: params.pin
        }
      }
    };
    if (params.pin) {
      req.req["pin"] = params.pin
    }
    return this.requestService.mainPostRequest(req);
  }

  // Edit Profile info
  editProfile(params) {
    let req = {
      url: "/api/subscribers/editProfile",
      req: {
        profile: {
          id: params.id,
          type: params.type,
          displayName: params.displayName,
          avatar: params.avatar,
          rating: params.rating,
          language: params.language,
          pinEnabled: params.pinEnabled
        }
      }
    };
    if (params.pin) {
      req.req["pin"] = params.pin
    }
    return this.requestService.mainPostRequest(req);
  }

  // Edit Profile info
  editProfileNew(params) {
    let req = {
      url: "/api/subscribers/editProfile",
      req: {
        profile: params
      }
    };
    return this.requestService.mainPostRequest(req);
  }

  // Delete Profile
  deleteProfile(params) {
    let req = {
      url: "/api/subscribers/deleteProfile",
      req: {
        profileId: params.id
      }
    };
    return this.requestService.mainPostRequest(req);
  }

  // Select profile
  switchProfile(params, userService) {
    let req = {
      url: "/api/subscribers/switchProfile",
      req: {
        profileId: params.id,
        language: params.language ? params.language : this.languageService.getDefaultLanguage(),
        pin: params.pin
      }
    };
    return new Promise(resolve => {
      // Switch Profile
      this.requestService.mainPostRequest(req).then((resp: any) => {
        resolve(resp);
      })
    });
  }

  // Change Profile Language
  changeProfileLanguage(params, userService) {
    let req = {
      url: "/api/subscribers/changeLanguage",
      req: {
        language: params.language
      }
    };
    return new Promise(resolve => {
      // Change Language
      this.requestService.mainPostRequest(req).then((resp: any) => {
        userService.getLoggedInStatus.next(true);
        // Update Token
        userService.getToken().then(respToken => {
          resolve(resp);
        });
      })
    });
  }

  /** Use code in order to login with the TV device
   * @param {sting} code
   * */
  loginCodeTv(code: string) {
    let req = {
      url: "/api/Auth/evaluateTvCode",
      req: {
        code: code
      }
    };
    return new Promise(resolve => {
      this.requestService.mainPostRequest(req).then((resp: any) => {
        resolve(resp);
      })
    });
  }

  /** Activate coupon code
   * @param {sting} coupon
   * */
  activateCoupon(coupon: string) {
    let req = {
      url: "/api/auth/activateCoupon",
      req: {
        coupon: coupon
      }
    };
    return new Promise(resolve => {
      this.requestService.mainPostRequest(req).then((resp: any) => {
        resolve(resp);
      })
    });
  }

  /** Update Subscriber Info
   * @param {Object} subscriberInfo (firstName,lastName,address)
   * */
  updateSubscriber(subscriberInfo: any) {
    let req = {
      url: "/api/subscribers/updateSubscriber",
      req: {
        firstName: subscriberInfo.firstName,
        lastName: subscriberInfo.lastName,
        address: subscriberInfo.address
      }
    };
    return new Promise(resolve => {
      this.requestService.mainPostRequest(req).then((resp: any) => {
        resolve(resp);
      })
    });
  }
}